import { useEffect, type ReactNode } from 'react';
import type { Grid, Screen, ScreenTile } from '@casa/model';
import { GroupTileView, TileView } from '@casa/tiles';
import { EDIT_ROWS_BELOW, EditLayer, type EditLayerProps } from '../editing/edit_layer.tsx';
import { useElementSize } from '../ui/use_element_size.ts';
import { GRID_SPACING, cellPitch, scrollingHeight, tileRect, type GridGeometry } from './grid.ts';

/** Edit mode on a screen: the selection, and what a drag or a tap changes. */
export type ScreenEditing = Omit<EditLayerProps, 'tiles' | 'grid' | 'scroller'> & {
    /** The grid as the screen lays it out, each time it changes: for the size of a tile on it. */
    onGeometry?: (grid: GridGeometry) => void;
};

/** A tile or a group at its place on the grid. */
function PlacedTile({ tile, grid, onTop = false }: { tile: ScreenTile; grid: GridGeometry; onTop?: boolean }) : ReactNode {
    const rect = tileRect(tile.layout, grid);

    return (
        <div style={{ position: 'absolute', ...rect, zIndex: onTop ? 1 : undefined }}>
            {tile.type === 'group' ? <GroupTileView group={tile} /> : <TileView tile={tile} />}
        </div>
    );
}

/**
 * One dashboard screen: its tiles placed on the dashboard's grid. A grid with rows fills the
 * parent; one without is as tall as its tiles need and scrolls vertically in the parent. Groups
 * (boxes with a title) are drawn behind every other tile. In edit mode a layer over the tiles takes
 * the taps (`EditLayer`), and the selected tile is drawn on top; a selected group stays behind the
 * tiles on it (issue #5), its outline and handles drawn by the edit layer above everything.
 */
export function ScreenView({ screen, grid: dashboardGrid, editing }: { screen: Screen; grid: Grid; editing?: ScreenEditing | undefined }) : ReactNode {
    const [ref, size] = useElementSize<HTMLDivElement>();
    const scrolling = dashboardGrid.rows === undefined;
    const grid = { ...GRID_SPACING, ...dashboardGrid, width: size.width, height: size.height };
    const tilesHeight = scrolling ? scrollingHeight(screen.tiles.map((tile) => tile.layout), grid) : undefined;
    const height = tilesHeight !== undefined && editing ? tilesHeight + EDIT_ROWS_BELOW * cellPitch(grid).down : tilesHeight;
    const groups = screen.tiles.filter((tile) => tile.type === 'group');
    const others = screen.tiles.filter((tile) => tile.type !== 'group');
    const { onGeometry, ...layerEditing } = editing ?? {};
    const geometry = JSON.stringify(grid);
    useEffect(() => {
        if (size.width > 0) {
            onGeometry?.(JSON.parse(geometry) as GridGeometry);
        }
    }, [geometry, onGeometry, size.width]);

    return (
        // The scrollbar's room is kept even while it is not needed: otherwise it would narrow the grid,
        // shrink the square cells and the grid with them, and go away again. No bounce at the ends
        // (index.css).
        <div
            ref={ref}
            style={{ position: 'absolute', inset: 0, overflowX: 'hidden', overflowY: scrolling ? 'auto' : 'hidden', overscrollBehavior: 'none', ...(scrolling ? { scrollbarGutter: 'stable' } : {}) }}
        >
            <div style={{ position: 'relative', height: height ?? '100%' }}>
                {size.width > 0 &&
                    [...groups, ...others].map((tile) => <PlacedTile key={tile.id} tile={tile} grid={grid} onTop={tile.type !== 'group' && tile.id === editing?.selectedId} />)}
                {size.width > 0 && editing && <EditLayer {...(layerEditing as Omit<ScreenEditing, 'onGeometry'>)} tiles={screen.tiles} grid={grid} scroller={ref.current} />}
                {editing && screen.tiles.length === 0 && (
                    <div style={{ position: 'absolute', top: 24, left: 16, right: 16, textAlign: 'center', color: '#9a9a9a', font: '16px system-ui, sans-serif', pointerEvents: 'none' }}>
                        This screen is empty. Add a tile with + in the menu.
                    </div>
                )}
            </div>
        </div>
    );
}
